/* eslint-disable @typescript-eslint/no-explicit-any */
import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError, AxiosRequestConfig } from 'axios'
import { OrdersService } from '~/shared/api/services/orders'
import { OrderStatusCounts } from '~/shared/api/services/orders/types'
import { WarehousesService } from '~/shared/api/services/warehouses'
import {
  Countries,
  DataGenerateCSV,
  Incoming,
  IncomingNumber,
  Loaders,
  Order,
  Stores,
  Suppliers,
  Warehouses,
  IncomingProduct as IncomingProductData,
  Assembly,
  AssemblyById,
  OrderBox,
  OrderData,
} from '~/shared/api/services/warehouses/types'
import { getBlobErrorMessage, handleAxiosError } from '~/shared/lib'

export type IncomingProductRow = Omit<IncomingProductData, 'quantity'> & {
  quantity: IncomingProductData['quantity'] | null
}

export class WarehousesModel {
  [key: string]: any

  warehouseError = false

  error404 = false

  incomingNumber: IncomingNumber | null = null

  incomingProductsCheck = false

  switcher = true

  assembly: Assembly | null = null

  orderStatusCounts: OrderStatusCounts = {
    legalEntityId: '',
    assembly: 0,
    dispatch: 0,
    express: 0,
  }

  currentAssemblyType: string | null = null

  incomingProducts: null | IncomingProductRow[] = null

  currentDataWarehouse: Incoming[] = []

  suppliers: Suppliers[] = []

  countries: Countries[] = []

  warehouses: Warehouses[] = []

  sortWarehouse: Warehouses[] = []

  stores: Stores[] = []

  orderBox: OrderBox | null = null

  assemblyById: AssemblyById | null = null

  data: Order | null = null

  dataGenerateCSV: DataGenerateCSV | null = null

  loaders: Loaders = {
    createNewEntrance: false,
    createSuppliers: false,
    loadMoreArrivalsById: false,
    loadArrivalsById: false,
    accept: false,
    delete: false,
    assembly: false,
    box: false,
    items: false,
    creatingAnAssembly: false,
    deferringAssembly: false,
  }

  requestSettings = {
    limit: 20,
    offset: 0,
    isAllLoaded: false,
    lastContentId: '',
  }

  barcode: Blob | null = null

  selectedOrder: OrderData | null = null

  type = ''

  selectedCardId: string[] = []

  constructor() {
    makeAutoObservable(this)
  }

  setSelectedOrder(order: OrderData) {
    this.selectedOrder = order
  }

  get assemblyTypes() {
    if (!this.assembly?.links) return []

    return Object.keys(this.assembly.links).filter((type) => type !== 'all')
  }

  getStatusCount(type: 'assembly' | 'dispatch' | 'express') {
    return this.orderStatusCounts[type] ?? 0
  }

  setOrderStatusCounts = (data: OrderStatusCounts) => {
    this.orderStatusCounts = data
  }

  setOrderStatusCount = (
    type: keyof Omit<OrderStatusCounts, 'legalEntityId'>,
    count: number,
  ) => {
    this.orderStatusCounts = {
      ...this.orderStatusCounts,
      [type]: count,
    }
  }

  setSwitcher(state: boolean) {
    runInAction(() => {
      this.switcher = state
    })
  }

  setIncomingProductsCheck(value: boolean) {
    runInAction(() => {
      this.incomingProductsCheck = value
    })
  }

  setIncomingProducts(data: IncomingProductRow[]) {
    runInAction(() => {
      this.incomingProducts = data
    })
  }

  activeLoader = (key: keyof Loaders) => {
    this.loaders = { ...this.loaders, [key]: true }
  }

  closeLoader = (key: keyof Loaders) => {
    this.loaders = { ...this.loaders, [key]: false }
  }

  getWarehouses = async (q?: string, shouldApplyResponse?: () => boolean) => {
    try {
      const params = q ? { params: { q } } : {}

      const { data } = await WarehousesService.getWarehouses(params)
      if (!shouldApplyResponse || shouldApplyResponse()) {
        runInAction(() => {
          this.warehouses = data
          this.sortWarehouse = [...data].sort((a, b) => {
            if (a.isDefault === b.isDefault) return 0
            if (a.isDefault) return -1
            return 1
          })
        })
      }
      return data
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.config.data)
      }
    }
  }

  getAssembly = async (type?: string) => {
    try {
      this.activeLoader('assembly')

      const config: AxiosRequestConfig = {
        params: {
          type,
        },
      }
      const { data } = await WarehousesService.getAssembly(config)
      runInAction(() => {
        this.currentAssemblyType = type ?? null
        this.assembly = data
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.config.data)
      }
    } finally {
      runInAction(() => {
        this.closeLoader('assembly')
      })
    }
  }

  fetchOrderStatusCounts = async () => {
    try {
      const { data } = await OrdersService.getStatusCounts()

      runInAction(() => {
        this.orderStatusCounts = data
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        return error.response?.config.data
      }
    }
  }

  getWarehouseID = async (
    id: string,
    action: 'loadMoreArrivalsById' | 'loadArrivalsById',
  ) => {
    this.activeLoader(action)
    if (this.requestSettings.lastContentId !== id) {
      this.requestSettings = {
        ...this.requestSettings,
        isAllLoaded: false,
        offset: 0,
      }
    }
    try {
      if (this.requestSettings.isAllLoaded) return
      const { data } = await WarehousesService.getWarehouseID({
        params: {
          limit: this.requestSettings.limit,
          offset: this.requestSettings.offset,
          warehouseId: id,
        },
      })

      runInAction(() => {
        if (this.requestSettings.lastContentId === id) {
          this.currentDataWarehouse = [...this.currentDataWarehouse, ...data]
        } else {
          this.currentDataWarehouse = data
        }
      })
      this.requestSettings = {
        ...this.requestSettings,
        isAllLoaded: data.length === 0,
        offset: this.requestSettings.offset + this.requestSettings.limit,
        lastContentId: id,
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.config.data)
      }
    } finally {
      this.closeLoader(action)
    }
  }

  getSuppliers = async () => {
    try {
      const { data } = await WarehousesService.getSuppliers()

      runInAction(() => {
        this.suppliers = data
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.config.data)
      }
    }
  }

  getCountries = async () => {
    try {
      const { data } = await WarehousesService.getCountries()
      runInAction(() => {
        this.countries = data.filter((el) => el.numberCode !== null)
      })
      return data
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.config.data)
      }
    }
  }

  getIncomingNumber = async (number: string) => {
    try {
      const { data } = await WarehousesService.getIncomingNumber(number)
      runInAction(() => {
        this.incomingNumber = data
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.config.data)
      }
    }
  }

  generateBarcode = async (id: string) => {
    runInAction(() => {
      this.barcodeLoading = true
    })

    try {
      const data = await WarehousesService.generateBarcode(id)

      runInAction(() => {
        this.barcode = data
      })

      return data
    } catch (error) {
      const errorMessage = await getBlobErrorMessage(error)
      throw new Error(errorMessage)
    } finally {
      runInAction(() => {
        this.barcodeLoading = false
      })
    }
  }

  getAssemblyById = async (id: string) => {
    try {
      this.activeLoader('items')
      const { data } = await WarehousesService.getAssemblyById(id)
      runInAction(() => {
        this.assemblyById = data
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.config.data)
      }
    } finally {
      runInAction(() => {
        this.closeLoader('items')
      })
    }
  }

  getOrderBox = async () => {
    try {
      this.activeLoader('box')
      const { data } = await WarehousesService.getOrderBox()
      runInAction(() => {
        this.orderBox = data
      })
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      runInAction(() => {
        this.closeLoader('box')
      })
    }
  }

  toggleCardSelection = (id: string) => {
    if (this.selectedCardId.includes(id)) {
      this.selectedCardId = this.selectedCardId.filter((el) => el !== id)
    } else {
      this.selectedCardId = [...this.selectedCardId, id]
    }
  }

  creatingAnAssembly = async (orderId: string, box: number) => {
    try {
      this.activeLoader('creatingAnAssembly')
      await WarehousesService.creatingAnAssembly(orderId, {
        box,
        isOrderPlatform: true,
        items: this.selectedCardId.map((itemId) => itemId || null),
      })
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      runInAction(() => {
        this.closeLoader('creatingAnAssembly')
      })
    }
  }

  deferAssemblyOrder = async (orderId: string) => {
    try {
      this.activeLoader('deferringAssembly')
      await WarehousesService.deferAssemblyOrder(orderId, true)

      runInAction(() => {
        if (this.selectedOrder?.id === orderId) {
          this.selectedOrder = { ...this.selectedOrder, isDeferred: true }
        }

        if (this.assemblyById?.order) {
          this.assemblyById = {
            ...this.assemblyById,
            order: {
              ...this.assemblyById.order,
              isDeferred: true,
            },
          }
        }
      })
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      runInAction(() => {
        this.closeLoader('deferringAssembly')
      })
    }
  }

  deleteEntrance = async (listNumber: string) => {
    try {
      await WarehousesService.deleteEntrance(listNumber, {
        incomingListNumber: this.incomingNumber?.number,
      })
      this.resetRequestSettings()
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    }
  }

  generateCSV = async (listNumber: string) => {
    try {
      this.activeLoader('delete')
      const { data } = await WarehousesService.generateCSV(listNumber, {
        incomingListNumber: this.incomingNumber?.number,
      })
      runInAction(() => {
        this.dataGenerateCSV = data
      })
      return data
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      runInAction(() => {
        this.closeLoader('delete')
      })
    }
  }

  accept = async (data: any) => {
    try {
      this.activeLoader('accept')
      const response = await WarehousesService.accept(data)
      this.requestSettings = {
        ...this.requestSettings,
        lastContentId: '',
        isAllLoaded: false,
        offset: 0,
      }
      return response.data
    } finally {
      runInAction(() => {
        this.closeLoader('accept')
      })
    }
  }

  resetSelectedCardId = () => {
    this.selectedCardId = []
  }

  resetBoxes = () => {
    this.orderBox = null
  }

  resetRequestSettings = () => {
    this.requestSettings = {
      limit: 20,
      offset: 0,
      isAllLoaded: false,
      lastContentId: '',
    }
  }
}
